/**
 * Unified Messages API
 * Унифицированный API слой для сообщений с type-safe endpoints
 *
 * Заменяет старую версию с дублированием URL-формирования
 */

import { useApiClient, type ApiResponse } from './useApiClient';
import { useFileUpload, type UploadOptions, type UploadResult } from './useFileUpload';
import { getCurrentUserId } from '../utils/userUtils.js';
import { logger } from '../utils/logger';

// ===== Types =====

export interface Conversation {
  id: string | number;
  contact_id: string | number;
  contact?: {
    id: string | number;
    full_name?: string;
    avatar?: string;
    age?: number;
    online?: boolean;
    premium?: boolean;
  };
  last_message?: {
    id?: string | number;
    text?: string;
    timestamp?: number;
    status?: string;
    hasMedia?: boolean;
    fromUserId?: string | number;
    toUserId?: string | number;
  };
  unread_count?: number;
}

export interface Message {
  id: string | number;
  temp_id?: string;
  text?: string;
  type: 'sent' | 'received';
  status?: 'pending' | 'delivered' | 'read' | 'error';
  timestamp: number;
  from_user_id?: string | number;
  to_user_id?: string | number;
  attachments?: Array<{
    id?: string | number;
    url?: string;
    preview?: string;
    type?: string;
    name?: string;
  }>;
  reactions?: Array<{
    emoji: string;
    count: number;
    user_ids?: (string | number)[];
  }>;
}

export interface SendMessagePayload {
  text?: string;
  contactId: string | number;
  tempId?: string | number;
  files?: File[];
  attachments?: File[];
  uploadedFiles?: unknown[];
  displayText?: string;
  onUploadProgress?: (percent: number) => void;
}

export interface SendMessageResult {
  success: boolean;
  messageId?: number | null;
  tempId?: string | number;
  method: 'websocket' | 'http' | 'upload';
  message?: string;
  needRefresh?: boolean;
  filePreviews?: unknown[];
}

export interface MarkReadPayload {
  chatId: string | number;
  messageIds?: (string | number)[];
}

// ===== Endpoints Configuration =====

const ENDPOINTS = {
  conversations: (query?: string) => ({
    path: 'messages/conversations',
    params: query ? { query } : undefined,
  }),
  messages: (chatId: string | number, cursor?: string | number) => ({
    path: 'messages/messages',
    params: { contactId: String(chatId), cursor },
  }),
  sendMessage: () => ({
    path: 'messages/create',
    method: 'POST' as const,
  }),
  uploadImages: () => ({
    path: 'messages/upload-images',
    method: 'POST' as const,
  }),
  markRead: () => ({
    path: 'messages/read-conversation',
    method: 'POST' as const,
  }),
} as const;

// ===== Retry Logic =====

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000];

async function withRetry<T>(
  fn: () => Promise<T>,
  options?: {
    maxRetries?: number;
    onRetry?: (attempt: number, error: Error) => void;
  }
): Promise<T> {
  const maxRetries = options?.maxRetries ?? MAX_RETRIES;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries - 1) {
        options?.onRetry?.(attempt + 1, lastError);
        const delay = RETRY_DELAYS[attempt] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

// ===== Composable =====

export function useMessagesApi() {
  const api = useApiClient();
  const fileUpload = useFileUpload();

  /**
   * Получить список диалогов
   */
  const getConversations = async (query?: string): Promise<ApiResponse<Conversation[]>> => {
    const endpoint = ENDPOINTS.conversations(query);
    const url = api.buildUrl(endpoint.path, endpoint.params);

    return api.get(url, { rateLimitKey: 'conversations' });
  };

  /**
   * Получить сообщения диалога
   */
  const getMessages = async (
    chatId: string | number,
    cursor?: string | number
  ): Promise<ApiResponse<Message[]>> => {
    const endpoint = ENDPOINTS.messages(chatId, cursor);
    const url = api.buildUrl(endpoint.path, endpoint.params);

    return api.get(url, { rateLimitKey: 'messages' });
  };

  /**
   * Отправить текстовое сообщение через WebSocket (приоритет) или HTTP
   */
  const sendTextMessage = async (payload: {
    text?: string;
    contactId: string | number;
    tempId?: string | number;
  }): Promise<SendMessageResult> => {
    const { text = '', contactId, tempId = `temp_${Date.now()}` } = payload;

    // Пробуем WebSocket первым (как AngularJS)
    const bootstrap = (window as any).wsBootstrap;
    if (bootstrap?.sendMessage && bootstrap?.isConnected?.()) {
      const messagePayload = {
        type: 'chat',
        channel: 'chat',
        data: {
          contactId,
          body: text,
          client_message_id: String(tempId),
          from_user_id: getCurrentUserId(),
          to_user_id: contactId,
          message: text,
          temp_id: String(tempId),
          timestamp: Math.floor(Date.now() / 1000),
        },
      };

      const sent = bootstrap.sendMessage(messagePayload, { immediate: true, queue: false });
      if (sent) {
        logger.log('MessagesApi: Sent via WebSocket');
        return {
          success: true,
          method: 'websocket',
          tempId,
        };
      }
    }

    // Fallback к HTTP
    const endpoint = ENDPOINTS.sendMessage();
    const url = api.buildUrl(endpoint.path);
    const csrfToken = api.getCsrfToken();

    const result = await withRetry(
      () =>
        api.post(url, {
          contactId: String(contactId),
          message: text,
          tempId: String(tempId),
          _csrf: csrfToken,
        }),
      {
        onRetry: (attempt, error) =>
          logger.warn(`MessagesApi: HTTP retry ${attempt}/${MAX_RETRIES}`, error),
      }
    );

    return {
      success: true,
      method: 'http',
      tempId,
      ...result,
    };
  };

  /**
   * Отправить сообщение с файлами (двухэтапный процесс)
   */
  const sendMessage = async (payload: SendMessagePayload): Promise<SendMessageResult> => {
    const { text, contactId, tempId, files, attachments, onUploadProgress } = payload;
    const allFiles = files || attachments || [];

    if (allFiles.length > 0) {
      // Есть файлы - используем двухэтапный процесс
      logger.log('MessagesApi: Processing media message');

      const uploadResult = await fileUpload.sendWithMedia(allFiles, contactId, String(tempId || `temp_${Date.now()}`), text, {
        onProgress: onUploadProgress,
      });

      return {
        success: uploadResult.success,
        method: 'upload',
        messageId: uploadResult.messageId,
        needRefresh: uploadResult.needRefresh,
        filePreviews: uploadResult.filePreviews,
        tempId: tempId || `temp_${Date.now()}`,
      };
    }

    // Только текст
    return sendTextMessage({ text, contactId, tempId });
  };

  /**
   * Загрузить изображения (alias для fileUpload.uploadImages)
   */
  const uploadImages = async (
    files: File[],
    contactId: string | number,
    tempId: string,
    options?: UploadOptions
  ): Promise<UploadResult> => {
    return fileUpload.uploadImages(files, contactId, tempId, options);
  };

  /**
   * Отметить сообщения прочитанными
   */
  const markRead = async (payload: MarkReadPayload): Promise<ApiResponse> => {
    const endpoint = ENDPOINTS.markRead();
    const url = api.buildUrl(endpoint.path);
    const csrfToken = api.getCsrfToken();

    const formData = {
      contactId: String(payload.chatId),
      ...(payload.messageIds?.length && {
        messageIds: payload.messageIds.join(','),
      }),
      _csrf: csrfToken,
    };

    return api.post(url, formData);
  };

  /**
   * Редактировать сообщение
   */
  const editMessage = async (params: { messageId: string | number; text: string }): Promise<any> => {
    const csrfToken = api.getCsrfToken();

    const body = new URLSearchParams();
    body.append('messageId', String(params.messageId));
    body.append('text', params.text);
    body.append('_csrf', csrfToken);

    const result = await withRetry(
      () =>
        api.post('/messages/edit-message', {
          messageId: String(params.messageId),
          text: params.text,
          _csrf: csrfToken,
        }),
      {
        onRetry: (attempt, error) =>
          logger.warn(`MessagesApi: Edit retry ${attempt}/${MAX_RETRIES}`, error),
      }
    );

    return result;
  };

  /**
   * Удалить сообщения
   */
  const deleteMessage = async (params: { messageIds: (string | number)[] }): Promise<ApiResponse> => {
    const csrfToken = api.getCsrfToken();

    const searchParams = new URLSearchParams();
    params.messageIds.forEach(id => searchParams.append('messages[]', String(id)));
    searchParams.append('_csrf', csrfToken);

    return api.post('/messages/delete', searchParams);
  };

  /**
   * Переключить реакцию на сообщение
   */
  const toggleReaction = async (params: { messageId: string | number; emoji: string }): Promise<ApiResponse> => {
    const csrfToken = api.getCsrfToken();

    const formData = {
      messageId: String(params.messageId),
      emoji: params.emoji,
      _csrf: csrfToken,
    };

    return api.post('/messages/toggle-reaction', formData);
  };

  return {
    getConversations,
    getMessages,
    sendMessage,
    sendTextMessage,
    uploadImages,
    markRead,
    editMessage,
    deleteMessage,
    toggleReaction,
  };
}

// Legacy export for backward compatibility
export { useMessagesApi as default };
